/**
 * The Findings page's global scope ⇄ URL. One scope governs the Queue,
 * Hotspots and Pages views and survives switching between them:
 *
 *   period   `last=30d` | `since=2026-10-03[&until=2026-10-15]` | `build=<uuid>`
 *   mode     `mode=new` (default: still happening)
 *   filters  `persona`, `cap` (+ `capScope`), `sev`, `lens` — the queue's own params
 *   build    `env`, `branch` (the Builds page's names), `committed=1`
 *
 * The last non-empty scope is remembered per project and restored when
 * Findings opens without one, always into the URL so it stays visible.
 */
import {
  type FindingsPeriod,
  type FindingsScope,
  findingsPeriodSchema,
  findingsScopeSchema,
} from "@iamu/shared";

export const PERIOD_PARAMS = ["last", "since", "until", "build", "mode"];

/** Every param the scope owns (period + the shared filters). */
export const SCOPE_PARAMS: readonly string[] = [
  ...PERIOD_PARAMS,
  "persona",
  "cap",
  "capScope",
  "sev",
  "lens",
  "env",
  "branch",
  "committed",
];

const LEGACY_WINDOW_DAYS = /^(\d{1,3})d$/;

/** The viewer's IANA zone, which calendar dates resolve in. */
export function viewerTimeZone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
  } catch {
    return "UTC";
  }
}

export function parsePeriod(params: URLSearchParams): FindingsPeriod | null {
  const build = params.get("build");
  const since = params.get("since");
  // `window=30d` is the hotspot map's pre-scope period (see migrateLegacyWindow).
  const last = (params.get("last") ?? params.get("window"))?.match(
    LEGACY_WINDOW_DAYS,
  );
  const candidate = build
    ? { kind: "sinceBuild", buildId: build }
    : since
      ? { kind: "range", since, until: params.get("until") ?? undefined }
      : last
        ? { kind: "last", days: Number(last[1]) }
        : null;
  const parsed = findingsPeriodSchema.safeParse(candidate);
  return parsed.success ? parsed.data : null;
}

/** The period fields of a queue/hotspot/pages query. */
export function periodQuery(
  params: URLSearchParams,
): Pick<FindingsScope, "period" | "periodMode" | "tz"> {
  const period = parsePeriod(params);
  if (!period) return {};
  return {
    period,
    periodMode: params.get("mode") === "new" ? "new" : undefined,
    tz: viewerTimeZone(),
  };
}

/** Write a period (or clear it) into params, in place. */
export function setPeriodParams(
  p: URLSearchParams,
  period: FindingsPeriod | null | undefined,
  mode?: FindingsScope["periodMode"],
): URLSearchParams {
  for (const name of PERIOD_PARAMS) p.delete(name);
  if (!period) return p;
  if (period.kind === "last") p.set("last", `${period.days}d`);
  else if (period.kind === "sinceBuild") p.set("build", period.buildId);
  else {
    p.set("since", period.since);
    if (period.until) p.set("until", period.until);
  }
  if (mode === "new") p.set("mode", "new");
  return p;
}

export function parseScope(params: URLSearchParams): FindingsScope {
  const raw: Record<string, unknown> = { ...periodQuery(params) };
  const persona = params.get("persona");
  const cap = params.get("cap");
  const capScope = params.get("capScope");
  const sev = params.get("sev");
  const lens = params.get("lens");
  const env = params.get("env");
  const branch = params.get("branch");
  if (persona) raw.personaId = persona;
  if (cap) raw.capabilityId = cap;
  if (capScope) raw.capabilityScope = capScope;
  if (sev) raw.severities = sev.split(",").filter(Boolean);
  if (lens) raw.lens = lens;
  if (env) raw.environment = env;
  if (branch) raw.branch = branch;
  if (params.get("committed") === "1") raw.committedOnly = true;
  const parsed = findingsScopeSchema.safeParse(raw);
  return parsed.success ? parsed.data : {};
}

/** The params with the scope replaced by `scope`. */
export function withScope(
  params: URLSearchParams,
  scope: FindingsScope,
): URLSearchParams {
  const p = new URLSearchParams(params);
  for (const name of SCOPE_PARAMS) p.delete(name);
  setPeriodParams(p, scope.period, scope.periodMode);
  if (scope.personaId) p.set("persona", scope.personaId);
  if (scope.capabilityId) {
    p.set("cap", scope.capabilityId);
    if (scope.capabilityScope) p.set("capScope", scope.capabilityScope);
  }
  if (scope.severities?.length) p.set("sev", scope.severities.join(","));
  if (scope.lens) p.set("lens", scope.lens);
  setBuildParams(p, scope);
  return p;
}

/** Write the build part of a scope into params, in place. */
export function setBuildParams(
  p: URLSearchParams,
  scope: Pick<FindingsScope, "environment" | "branch" | "committedOnly">,
): URLSearchParams {
  if (scope.environment) p.set("env", scope.environment);
  if (scope.branch) p.set("branch", scope.branch);
  if (scope.committedOnly) p.set("committed", "1");
  return p;
}

/** Only the scope's params. */
export function scopeParams(params: URLSearchParams): URLSearchParams {
  const p = new URLSearchParams();
  for (const name of SCOPE_PARAMS) {
    const v = params.get(name);
    if (v) p.set(name, v);
  }
  return p;
}

export function hasScope(params: URLSearchParams): boolean {
  return SCOPE_PARAMS.some((name) => params.has(name));
}

/** Anything narrowed (the "All time, everyone" scope is empty). */
export function isScoped(scope: FindingsScope): boolean {
  return Boolean(
    scope.period ||
      scope.personaId ||
      scope.capabilityId ||
      scope.severities?.length ||
      scope.lens ||
      scope.environment ||
      scope.branch ||
      scope.committedOnly,
  );
}

/** The scope narrows runs by their build (environment, branch, committed). */
export function hasBuildScope(scope: FindingsScope): boolean {
  return Boolean(scope.environment || scope.branch || scope.committedOnly);
}

/** Short phrase of the build scope ("on staging · branch main"), or null. */
export function buildScopeLabel(scope: FindingsScope): string | null {
  const parts: string[] = [];
  if (scope.environment)
    parts.push(`on ${environmentLabel(scope.environment)}`);
  if (scope.branch) parts.push(`branch ${scope.branch}`);
  if (scope.committedOnly) parts.push("committed runs only");
  return parts.length ? parts.join(" · ") : null;
}

/**
 * Old hotspot links carried the period in `window=30d`; it is the scope's
 * `last=30d` now (and the hotspot window keeps runs only). Null when the
 * params need no migration.
 */
export function migrateLegacyWindow(
  params: URLSearchParams,
): URLSearchParams | null {
  const days = params.get("window")?.match(LEGACY_WINDOW_DAYS);
  if (!days) return null;
  const p = new URLSearchParams(params);
  p.delete("window");
  if (!parsePeriod(p)) p.set("last", `${days[1]}d`);
  return p;
}

// ── Remembered per project ──────────────────────────────────────────

const storageKey = (projectId: string) => `iamu.findingsScope.${projectId}`;

/** The remembered scope params, or null. */
export function readStoredScope(projectId: string): URLSearchParams | null {
  try {
    const raw = window.localStorage.getItem(storageKey(projectId));
    if (!raw) return null;
    const p = scopeParams(new URLSearchParams(raw));
    return hasScope(p) ? p : null;
  } catch {
    return null;
  }
}

/** Remember the scope (an empty one forgets it). */
export function storeScope(projectId: string, params: URLSearchParams): void {
  try {
    const p = scopeParams(params);
    if (hasScope(p)) {
      window.localStorage.setItem(storageKey(projectId), p.toString());
    } else {
      window.localStorage.removeItem(storageKey(projectId));
    }
  } catch {
    // Storage blocked (private window, previews): the URL still holds it.
  }
}

// ── Labels ──────────────────────────────────────────────────────────

export const QUICK_PERIODS = [7, 30, 90] as const;

/** `2026-10-03` → "3 Oct" (with the year when it isn't this year). */
export function formatDay(iso: string, now = new Date()): string {
  const [y, m, d] = iso.split("-").map(Number);
  const date = new Date(y, m - 1, d);
  return date.toLocaleDateString(undefined, {
    day: "numeric",
    month: "short",
    ...(y === now.getFullYear() ? {} : { year: "numeric" }),
  });
}

/** Local calendar date as `YYYY-MM-DD`. */
export function isoDay(date: Date): string {
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

/** `https://staging.example.com` → `staging.example.com`; names stay. */
export function environmentLabel(env: string): string {
  return env.replace(/^https?:\/\//, "");
}

/** Short label of a period, for the pill and the scope sentence. */
export function periodLabel(
  period: FindingsPeriod | null | undefined,
  buildLabel?: string | null,
): string {
  if (!period) return "All time";
  if (period.kind === "last") return `Last ${period.days} days`;
  if (period.kind === "sinceBuild") {
    return buildLabel ? `Since build ${buildLabel}` : "Since a build";
  }
  if (!period.until) return `Since ${formatDay(period.since)}`;
  return period.since === period.until
    ? `On ${formatDay(period.since)}`
    : `${formatDay(period.since)} – ${formatDay(period.until)}`;
}
